'use client';
import clsx from 'clsx';
import { useState, useEffect, startTransition, useMemo, useRef } from 'react';
import usePreferencesStore from '@/features/Preferences/store/usePreferencesStore';
import { useCrazyMode } from '@/features/CrazyMode';
import { useShallow } from 'zustand/react/shallow';
import { usePathname } from 'next/navigation';
import { ScrollRestoration } from 'next-scroll-restoration';
import WelcomeModal from '@/shared/ui-composite/Modals/WelcomeModal';
import { DonationModal } from '@/features/Preferences';
import useOnboardingStore from '@/shared/store/useOnboardingStore';
import {
  AchievementNotificationContainer,
  AchievementIntegration,
  AchievementPromptsContainer,
} from '@/features/Achievements/components';
import {
  applyTheme,
  isPremiumThemeId,
  getThemeDefaultWallpaperId,
} from '@/features/Preferences/data/themes/themes';
import { getWallpaperById } from '@/features/Preferences/data/wallpapers/wallpapers';
import BackToTop from '@/shared/ui-composite/navigation/BackToTop';
import MobileBottomBar from '@/shared/ui-composite/layout/BottomBar';
import { useVisitTracker } from '@/features/Progress/hooks/useVisitTracker';
import { getGlobalAdaptiveSelector } from '@/shared/utils/adaptiveSelection';
import GlobalAudioController from '@/shared/ui-composite/layout/GlobalAudioController';
import { useClick } from '@/shared/hooks/generic/useAudio';
import ServiceWorkerRegistration from '@/shared/ui-composite/ServiceWorkerRegistration';
import VisualEffectsRenderer from '@/features/Preferences/components/renderers/VisualEffectsRenderer';
import TransitionAdvertisementOverlay, {
  isTransitionAdvertisementEnabled,
} from '@/shared/ui-composite/Game/TransitionAdvertisementOverlay';
import { STREAK_MILESTONE_DONATION_EVENT } from '@/shared/ui-composite/Game/StreakMilestoneOverlay';

// Initialize adaptive selector early to load persisted weights from IndexedDB
// This runs once at module load time, ensuring weights are ready before games start
// Deployment marker: refresh production after server-only bug report pipeline setup.
if (typeof window !== 'undefined') {
  const selector = getGlobalAdaptiveSelector();
  selector.ensureLoaded().catch(console.error);
}

// Define a type for the font object for clarity, adjust as needed
type FontObject = {
  name: string;
  font: {
    className: string;
  };
};

// Module-level cache for fonts (persists across component remounts)
let fontsCache: FontObject[] | null = null;
let fontsLoadingPromise: Promise<FontObject[]> | null = null;

const loadFontsModule = async (): Promise<FontObject[]> => {
  if (fontsCache) return fontsCache;
  if (fontsLoadingPromise) return fontsLoadingPromise;

  fontsLoadingPromise = import('@/features/Preferences/data/fonts/fonts').then(
    module => {
      fontsCache = module.default;
      fontsLoadingPromise = null;
      return module.default;
    },
  );

  return fontsLoadingPromise;
};

export default function ClientLayout({
  children,
}: Readonly<{
  children: React.ReactNode;
}>) {
  // Redundant comment for deployment trigger
  // Trigger redeployment - 2026-02-26
  // Redundant no-op comment to force a fresh Vercel deployment
  // Force deployment check - v2
  // Deployment trigger #3
  // Deployment trigger #4 - keep this harmless no-op comment
  // Redeploy trigger - redundant whitespaceless comment
  // Redeploy trigger - April 24, 2026
  // Redeploy trigger - R2 wallpaper pipeline rollout, June 20, 2026

  // Redeploy trigger - second redundant comment to force redeploy (no-op)
  // Redeploy trigger - third redundant comment to test Vercel Edge outage (March 2, 2026)
  const { theme, font } = usePreferencesStore(
    useShallow(state => ({ theme: state.theme, font: state.font })),
  );

  // Crazy Mode Integration
  const { isCrazyMode, activeThemeId, activeFontName, randomize } =
    useCrazyMode();

  // Determine effective theme and font
  const effectiveTheme = isCrazyMode && activeThemeId ? activeThemeId : theme;
  const effectiveFont = isCrazyMode && activeFontName ? activeFontName : font;

  // 3. Create state to hold the fonts module
  const [fontsModule, setFontsModule] = useState<FontObject[] | null>(null);
  const [isDonationModalOpen, setIsDonationModalOpen] = useState(false);
  const [isTransitionAdvertisementOpen, setIsTransitionAdvertisementOpen] =
    useState(false);
  const hasSeenWelcome = useOnboardingStore(state => state.hasSeenWelcome);

  // Memoize fontClassName calculation to prevent recalculation on every render (5-10ms savings)
  const fontClassName = useMemo(() => {
    if (!fontsModule) return '';
    return (
      fontsModule.find((fontObj: FontObject) => effectiveFont === fontObj.name)
        ?.font.className || ''
    );
  }, [fontsModule, effectiveFont]);

  const pathname = usePathname();
  const previousPathnameRef = useRef<string | null>(null);

  useEffect(() => {
    const showDonationModal = () => setIsDonationModalOpen(true);

    window.addEventListener(
      STREAK_MILESTONE_DONATION_EVENT,
      showDonationModal,
    );

    return () => {
      window.removeEventListener(
        STREAK_MILESTONE_DONATION_EVENT,
        showDonationModal,
      );
    };
  }, []);

  useEffect(() => {
    const previousPathname = previousPathnameRef.current;
    const isTrainingRoute = (path: string) =>
      /^\/(?:[a-z]{2}\/)?(?:kana|kanji|vocabulary)\/train(?:\/|$)/.test(path);
    const isDojoMenuRoute = (path: string) =>
      /^\/(?:[a-z]{2}\/)?(?:kana|kanji|vocabulary)\/?$/.test(path);

    let showAdvertisementTimer: ReturnType<typeof setTimeout> | undefined;

    if (
      previousPathname &&
      isTrainingRoute(previousPathname) &&
      isDojoMenuRoute(pathname) &&
      isTransitionAdvertisementEnabled('after')
    ) {
      showAdvertisementTimer = setTimeout(() => {
        setIsTransitionAdvertisementOpen(true);
      }, 0);
    }

    previousPathnameRef.current = pathname;

    return () => {
      if (showAdvertisementTimer) clearTimeout(showAdvertisementTimer);
    };
  }, [pathname]);

  useEffect(() => {
    const isDev = process.env.NODE_ENV === 'development';
    const isPreviewDeployment =
      process.env.NODE_ENV === 'production' &&
      process.env.NEXT_PUBLIC_VERCEL_ENV !== 'production';
    const isTargetRoute = /\/(kana|kanji|vocabulary)(\/|$)/.test(pathname);
    const isPreferencesRoute = /\/preferences(\/|$)/.test(pathname);
    const isProgressRoute = /\/progress(\/|$)/.test(pathname);
    const isBaseRoute =
      pathname === '/' || pathname === '/en' || pathname === '/ja';
    const donationLastPathKey = 'donation-modal-last-pathname';
    const donationCycleCountKey = 'donation-modal-cycle-count';
    const previousPathname =
      typeof window !== 'undefined'
        ? sessionStorage.getItem(donationLastPathKey)
        : null;

    if (isBaseRoute) {
      if (typeof window !== 'undefined') {
        sessionStorage.setItem(donationLastPathKey, pathname);
      }
      setIsDonationModalOpen(false);
      return;
    }

    // TEMPORARILY COMMENTED OUT: auto-show on preferences in dev/preview
    // if ((isDev || isPreviewDeployment) && isPreferencesRoute) {
    //   if (typeof window !== 'undefined') {
    //     sessionStorage.setItem(donationLastPathKey, pathname);
    //   }
    //   const timer = setTimeout(() => {
    //     setIsDonationModalOpen(true);
    //   }, 500);
    //   return () => clearTimeout(timer);
    // }

    const cameFromHome =
      previousPathname === '/' ||
      previousPathname === '/en' ||
      previousPathname === '/ja';

    const shouldCycle =
      (hasSeenWelcome && isTargetRoute && cameFromHome) ||
      (hasSeenWelcome && (isPreferencesRoute || isProgressRoute));

    if (shouldCycle) {
      const nextCount =
        Number(
          typeof window !== 'undefined'
            ? sessionStorage.getItem(donationCycleCountKey)
            : null,
        ) + 1;

      if (typeof window !== 'undefined') {
        sessionStorage.setItem(donationCycleCountKey, String(nextCount));
        sessionStorage.setItem(donationLastPathKey, pathname);
      }

      if (nextCount % 2 === 0) {
        const timer = setTimeout(() => {
          setIsDonationModalOpen(true);
        }, 500);
        return () => clearTimeout(timer);
      }
      return;
    }

    if (typeof window !== 'undefined') {
      sessionStorage.setItem(donationLastPathKey, pathname);
    }
  }, [hasSeenWelcome, pathname]);

  useEffect(() => {
    startTransition(() => {
      applyTheme(effectiveTheme); // This now sets both CSS variables AND data-theme attribute
    });

    if (typeof window !== 'undefined') {
      window.history.scrollRestoration = 'manual';
    }
  }, [effectiveTheme]);

  // Trigger randomization on page navigation
  useEffect(() => {
    if (isCrazyMode) {
      randomize();
    }
  }, [pathname, isCrazyMode, randomize]);

  // Load fonts using cached loader - only in production
  useEffect(() => {
    let isMounted = true;

    const initFonts = async () => {
      try {
        const fonts = await loadFontsModule();
        if (isMounted) {
          setFontsModule(fonts);
        }
      } catch (err) {
        console.error('Failed to dynamically load fonts:', err);
      }
    };

    void initFonts();

    return () => {
      isMounted = false;
    };
  }, []);

  // Track user visits for streak feature
  useVisitTracker();

  // Global typing sound: play click when user types in any input element.
  // playClick already respects the global silentMode setting via useAudioPreferences,
  // so the effect re-registers automatically whenever that preference changes.
  const { playClick } = useClick();
  useEffect(() => {
    const IGNORED_KEYS = new Set([
      'Shift',
      'Control',
      'Alt',
      'Meta',
      'Tab',
      'Escape',
      'Enter',
      'ArrowUp',
      'ArrowDown',
      'ArrowLeft',
      'ArrowRight',
      'Backspace',
      'Delete',
      'Home',
      'End',
      'PageUp',
      'PageDown',
      'CapsLock',
      'F1',
      'F2',
      'F3',
      'F4',
      'F5',
      'F6',
      'F7',
      'F8',
      'F9',
      'F10',
      'F11',
      'F12',
    ]);

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.repeat || IGNORED_KEYS.has(e.key)) return;
      const el = document.activeElement;
      if (!el) return;
      const tag = el.tagName;
      if (
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        tag === 'SELECT' ||
        (el as HTMLElement).isContentEditable
      ) {
        playClick();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [playClick]);

  // Note: Web Audio API context resumption is handled in useAudio.ts

  return (
    <div
      data-scroll-restoration-id='container'
      className={clsx(
        'min-h-[100dvh] max-w-[100dvw] bg-(--background-color) text-(--main-color)',
        fontClassName,
      )}
      style={{
        height: '100dvh',
        overflowY: 'auto',
        ...(() => {
          if (!isPremiumThemeId(effectiveTheme)) return {};

          const wallpaperId = getThemeDefaultWallpaperId(effectiveTheme);
          if (!wallpaperId) return {};

          const wallpaper = getWallpaperById(wallpaperId);
          if (!wallpaper) return {};

          // Use image-set for AVIF + WebP fallback
          const backgroundImage = wallpaper.urlWebp
            ? `image-set(url('${wallpaper.url}') type('image/avif'), url('${wallpaper.urlWebp}') type('image/webp'))`
            : `url('${wallpaper.url}')`;

          return {
            backgroundImage,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundAttachment: 'fixed',
          };
        })(),
      }}
    >
      <GlobalAudioController />
      <ServiceWorkerRegistration />
      <VisualEffectsRenderer />
      {children}
      <TransitionAdvertisementOverlay
        isOpen={isTransitionAdvertisementOpen}
        placement='after'
        onDismiss={() => setIsTransitionAdvertisementOpen(false)}
      />
      <ScrollRestoration />
      <WelcomeModal />
      <DonationModal
        open={isDonationModalOpen}
        onOpenChange={open => {
          setIsDonationModalOpen(open);
          if (!open && typeof window !== 'undefined') {
            sessionStorage.setItem('donation-modal-seen', 'true');
          }
        }}
      />
      <AchievementNotificationContainer />
      {/* hamza */}
      <AchievementPromptsContainer />
      <AchievementIntegration />
      <BackToTop />
      <MobileBottomBar />
    </div>
  );
}
